import React from 'react';

export interface RequestGuard {
    // Starts a request and invalidates every earlier one. The returned
    // `isCurrent()` is true only until the next `begin()` / `invalidate()`.
    begin(): () => boolean;
    // Invalidates every in-flight request without starting a new one.
    invalidate(): void;
}

/**
 * Generation counter: lets an async answer check "am I still the latest
 * request?" before it is allowed to change state. Everything in flight is
 * invalidated when `scopeKey` (session / journey) changes and on unmount.
 */
export const useRequestGuard = (scopeKey: string): RequestGuard => {
    const generation = React.useRef(0);

    const guard = React.useMemo<RequestGuard>(
        () => ({
            begin: () => {
                const mine = ++generation.current;
                return () => mine === generation.current;
            },
            invalidate: () => {
                generation.current++;
            },
        }),
        [],
    );

    // Layout cleanup runs synchronously at commit, before any promise callback,
    // so no old answer can slip in between a scope change and its effects.
    React.useLayoutEffect(
        () => () => {
            generation.current++;
        },
        [scopeKey],
    );

    return guard;
};
